<template>
    <div class="min-h-screen bg-gray-900 text-white p-3 sm:p-6 lg:p-8 mb-4 sm:mb-8">
        <div class="max-w-4xl mx-auto">
            <h1 class="text-2xl sm:text-3xl font-bold mb-4 sm:mb-8">Hero Profile Form</h1>

            <form @submit.prevent="handleSubmit" class="space-y-4 sm:space-y-8">
                <!-- Main Section -->
                <section class="bg-gray-800 p-3 sm:p-6 rounded-lg">
                    <h2 class="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Main Information</h2>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                            <label for="name" class="block text-sm font-medium mb-1">Name <span class="text-red-500">*</span></label>
                            <input v-model="form.name" id="name" type="text" required
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2.5 sm:p-2 text-white text-base sm:text-sm min-h-[44px] touch-manipulation" />
                        </div>
                        <div>
                            <label for="type" class="block text-sm font-medium mb-1">Types</label>
                            <div>
                                <input v-model="typeInput" @keyup="addType" @keydown.enter.prevent id="type" type="text"
                                    placeholder="Enter type and press Shift + Enter"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2" />
                                <p v-if="error.type" class="text-red-400 text-sm">{{ error.type }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div v-for="(type, index) in form.type" :key="index"
                                        class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                                        <span>{{ type }}</span>
                                        <button type="button" @click="removeItem('type')(index)"
                                            class="text-red-400 hover:text-red-300">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- About Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">About</h2>
                    <div class="space-y-4">
                        <div>
                            <div class="flex flex-col items-center space-y-4">
                                <div class="relative h-[200px] w-[200px] sm:h-[300px] sm:w-[300px] rounded-lg bg-gray-700 border-2 border-gray-600 overflow-hidden cursor-pointer touch-manipulation"
                                    @click="$refs.fileInput.click()" :class="{ 'border-red-400': errors.image }">
                                    <img v-if="previewImage || form.imageURL" :src="previewImage || form.imageURL"
                                        alt="Profile Preview" class="h-full w-full object-cover" />
                                    <div v-else
                                        class="flex h-full w-full items-center justify-center text-gray-400 text-sm font-medium">
                                        Upload Image
                                    </div>
                                    <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/jpg"
                                        @change="handleImageChange" class="hidden" />
                                </div>
                                <p v-if="errors.image" class="text-sm text-red-400">{{ errors.image }}</p>
                                <p v-if="imageUploading" class="text-sm text-blue-400">Uploading image...</p>
                            </div>
                        </div>
                        <div>
                            <label for="aboutTxt" class="block text-sm font-medium mb-1">About Description</label>
                            <textarea v-model="form.aboutTxt" id="aboutTxt" rows="4"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white"></textarea>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label for="age" class="block text-sm font-medium mb-1">Date of Birth <span class="text-red-500">*</span></label>
                                <input v-model="form.age" id="age" type="date" required
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                            </div>
                            <div>
                                <label for="gender" class="block text-sm font-medium mb-1">Gender</label>
                                <select v-model="form.gender" id="gender"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white">
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                        </div>
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label for="nationality" class="block text-sm font-medium mb-1">Nationality</label>
                                <input v-model="form.nationality" id="nationality" type="text"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                            </div>
                            <div>
                                <label for="available" class="block text-sm font-medium mb-1">Availability</label>
                                <select v-model="form.available" id="available"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white">
                                    <option value="">Select Availability</option>
                                    <option value="Full-time">Full-time</option>
                                    <option value="Part-time">Part-time</option>
                                    <option value="Freelance">Freelance</option>
                                    <option value="Not Available">Not Available</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Contact Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Contact Information</h2>
                    <div class="space-y-4">
                        <div>
                            <label for="phone" class="block text-sm font-medium mb-1">Phone</label>
                            <input v-model="form.phone" id="phone" type="tel"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="email" class="block text-sm font-medium mb-1">Email <span class="text-red-500">*</span></label>
                            <input v-model="form.email" id="email" type="email" required
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="address" class="block text-sm font-medium mb-1">Address</label>
                            <input v-model="form.address" id="address" type="text"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                    </div>
                </section>

                <!-- Skills Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Skills Information</h2>
                    <div class="space-y-4">
                        <div>
                            <label for="frontends" class="block text-sm font-medium mb-1">Frontend Skills</label>
                            <div>
                                <input v-model="frontendsInput" @keyup="addFrontends" @keydown.enter.prevent
                                    id="frontends" type="text" placeholder="Enter frontend and press Shift + Enter"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2" />
                                <p v-if="error.frontends" class="text-red-400 text-sm">{{ error.frontends }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div v-for="(frontends, index) in form.frontends" :key="index"
                                        class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                                        <span>{{ frontends }}</span>
                                        <button type="button" @click="removeFrontends(index)"
                                            class="text-red-400 hover:text-red-300">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label for="backends" class="block text-sm font-medium mb-1">Backend Skills</label>
                            <div>
                                <input v-model="backendsInput" @keyup="addBackends" @keydown.enter.prevent id="backends"
                                    type="text" placeholder="Enter backend and press Shift + Enter"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2" />
                                <p v-if="error.backends" class="text-red-400 text-sm">{{ error.backends }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div v-for="(backends, index) in form.backends" :key="index"
                                        class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                                        <span>{{ backends }}</span>
                                        <button type="button" @click="removeBackends(index)"
                                            class="text-red-400 hover:text-red-300">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label for="databases" class="block text-sm font-medium mb-1">Database Skills</label>
                            <div>
                                <input v-model="databasesInput" @keyup="addDatabases" @keydown.enter.prevent
                                    id="databases" type="text" placeholder="Enter database and press Shift + Enter"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2" />
                                <p v-if="error.databases" class="text-red-400 text-sm">{{ error.databases }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div v-for="(databases, index) in form.databases" :key="index"
                                        class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                                        <span>{{ databases }}</span>
                                        <button type="button" @click="removeDatabases(index)"
                                            class="text-red-400 hover:text-red-300">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label for="tools" class="block text-sm font-medium mb-1">Tools</label>
                            <div>
                                <input v-model="toolsInput" @keyup="addTools" @keydown.enter.prevent id="tools"
                                    type="text" placeholder="Enter tool and press Shift + Enter"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2" />
                                <p v-if="error.tools" class="text-red-400 text-sm">{{ error.tools }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div v-for="(tools, index) in form.tools" :key="index"
                                        class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                                        <span>{{ tools }}</span>
                                        <button type="button" @click="removeTools(index)"
                                            class="text-red-400 hover:text-red-300">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label for="graphics" class="block text-sm font-medium mb-1">Graphic Skills</label>
                            <div>
                                <input v-model="graphicsInput" @keyup="addGraphics" @keydown.enter.prevent id="graphics"
                                    type="text" placeholder="Enter graphic and press Shift + Enter"
                                    class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white mb-2" />
                                <p v-if="error.graphics" class="text-red-400 text-sm">{{ error.graphics }}</p>
                                <div class="flex flex-wrap gap-2">
                                    <div v-for="(graphics, index) in form.graphics" :key="index"
                                        class="bg-gray-600 text-white px-3 py-1 rounded-full flex items-center space-x-2">
                                        <span>{{ graphics }}</span>
                                        <button type="button" @click="removeGraphics(index)"
                                            class="text-red-400 hover:text-red-300">
                                            ×
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Social Links Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Social Links</h2>
                    <div class="space-y-4">
                        <div>
                            <label for="facebookURL" class="block text-sm font-medium mb-1">Facebook URL</label>
                            <input v-model="form.facebookURL" id="facebookURL" type="url"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="telegramURL" class="block text-sm font-medium mb-1">Telegram URL</label>
                            <input v-model="form.telegramURL" id="telegramURL" type="url"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="githubURL" class="block text-sm font-medium mb-1">GitHub URL</label>
                            <input v-model="form.githubURL" id="githubURL" type="url"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="linkedinURL" class="block text-sm font-medium mb-1">LinkedIn URL</label>
                            <input v-model="form.linkedinURL" id="linkedinURL" type="url"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                    </div>
                </section>

                <!-- Statistics Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Statistics</h2>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label for="projectsCount" class="block text-sm font-medium mb-1">Projects Completed</label>
                            <input v-model="form.projectsCount" id="projectsCount" type="number" min="0"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="experienceYears" class="block text-sm font-medium mb-1">Years of Experience</label>
                            <input v-model="form.experienceYears" id="experienceYears" type="number" min="0"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="clientsCount" class="block text-sm font-medium mb-1">Happy Clients</label>
                            <input v-model="form.clientsCount" id="clientsCount" type="number" min="0"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                        <div>
                            <label for="certificationsCount" class="block text-sm font-medium mb-1">Certifications</label>
                            <input v-model="form.certificationsCount" id="certificationsCount" type="number" min="0"
                                class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                        </div>
                    </div>
                </section>

                <!-- Experience Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Work Experience</h2>
                    <div class="space-y-4">
                        <div v-for="(exp, index) in form.experience" :key="index" class="border border-gray-600 p-4 rounded-lg">
                            <div class="flex justify-between items-start mb-3">
                                <h3 class="text-lg font-medium">Experience {{ index + 1 }}</h3>
                                <button type="button" @click="removeExperience(index)" class="text-red-400 hover:text-red-300">
                                    Remove
                                </button>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label :for="`expTitle${index}`" class="block text-sm font-medium mb-1">Job Title</label>
                                    <input v-model="exp.title" :id="`expTitle${index}`" type="text"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`expCompany${index}`" class="block text-sm font-medium mb-1">Company</label>
                                    <input v-model="exp.company" :id="`expCompany${index}`" type="text"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`expStartDate${index}`" class="block text-sm font-medium mb-1">Start Date</label>
                                    <input v-model="exp.startDate" :id="`expStartDate${index}`" type="date"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`expEndDate${index}`" class="block text-sm font-medium mb-1">End Date</label>
                                    <input v-model="exp.endDate" :id="`expEndDate${index}`" type="date"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div class="md:col-span-2">
                                    <label :for="`expDescription${index}`" class="block text-sm font-medium mb-1">Description</label>
                                    <textarea v-model="exp.description" :id="`expDescription${index}`" rows="3"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white"></textarea>
                                </div>
                            </div>
                        </div>
                        <button type="button" @click="addExperience" 
                                class="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                            + Add Experience
                        </button>
                    </div>
                </section>

                <!-- Education Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Education</h2>
                    <div class="space-y-4">
                        <div v-for="(edu, index) in form.education" :key="index" class="border border-gray-600 p-4 rounded-lg">
                            <div class="flex justify-between items-start mb-3">
                                <h3 class="text-lg font-medium">Education {{ index + 1 }}</h3>
                                <button type="button" @click="removeEducation(index)" class="text-red-400 hover:text-red-300">
                                    Remove
                                </button>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label :for="`eduDegree${index}`" class="block text-sm font-medium mb-1">Degree</label>
                                    <input v-model="edu.degree" :id="`eduDegree${index}`" type="text"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`eduInstitution${index}`" class="block text-sm font-medium mb-1">Institution</label>
                                    <input v-model="edu.institution" :id="`eduInstitution${index}`" type="text"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`eduStartDate${index}`" class="block text-sm font-medium mb-1">Start Date</label>
                                    <input v-model="edu.startDate" :id="`eduStartDate${index}`" type="date"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`eduEndDate${index}`" class="block text-sm font-medium mb-1">End Date</label>
                                    <input v-model="edu.endDate" :id="`eduEndDate${index}`" type="date"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div class="md:col-span-2">
                                    <label :for="`eduDescription${index}`" class="block text-sm font-medium mb-1">Description</label>
                                    <textarea v-model="edu.description" :id="`eduDescription${index}`" rows="3"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white"></textarea>
                                </div>
                            </div>
                        </div>
                        <button type="button" @click="addEducation" 
                                class="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                            + Add Education
                        </button>
                    </div>
                </section>

                <!-- Achievements Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Achievements & Certifications</h2>
                    <div class="space-y-4">
                        <div v-for="(achievement, index) in form.achievements" :key="index" class="border border-gray-600 p-4 rounded-lg">
                            <div class="flex justify-between items-start mb-3">
                                <h3 class="text-lg font-medium">Achievement {{ index + 1 }}</h3>
                                <button type="button" @click="removeAchievement(index)" class="text-red-400 hover:text-red-300">
                                    Remove
                                </button>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label :for="`achievementTitle${index}`" class="block text-sm font-medium mb-1">Title</label>
                                    <input v-model="achievement.title" :id="`achievementTitle${index}`" type="text"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div>
                                    <label :for="`achievementDate${index}`" class="block text-sm font-medium mb-1">Date</label>
                                    <input v-model="achievement.date" :id="`achievementDate${index}`" type="date"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                </div>
                                <div class="md:col-span-2">
                                    <label :for="`achievementDescription${index}`" class="block text-sm font-medium mb-1">Description</label>
                                    <textarea v-model="achievement.description" :id="`achievementDescription${index}`" rows="3"
                                        class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white"></textarea>
                                </div>
                            </div>
                        </div>
                        <button type="button" @click="addAchievement" 
                                class="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                            + Add Achievement
                        </button>
                    </div>
                </section>

                <!-- Testimonials Section -->
                <section class="bg-gray-800 p-6 rounded-lg">
                    <h2 class="text-xl font-semibold mb-4">Client Testimonials</h2>
                    <div class="space-y-4">
                        <div v-for="(testimonial, index) in form.testimonials" :key="index" class="border border-gray-600 p-4 rounded-lg">
                            <div class="flex justify-between items-start mb-3">
                                <h3 class="text-lg font-medium">Testimonial {{ index + 1 }}</h3>
                                <button type="button" @click="removeTestimonial(index)" class="text-red-400 hover:text-red-300">
                                    Remove
                                </button>
                            </div>
                            <div class="space-y-4">
                                <div>
                                    <label class="block text-sm font-medium mb-1">Client Logo</label>
                                    <div v-if="testimonial.imageURL || testimonial.previewImage" class="flex flex-col items-center space-y-2">
                                        <img :src="testimonial.previewImage || testimonial.imageURL" 
                                            alt="Client Logo" 
                                            class="h-20 w-20 object-contain rounded"
                                            @error="handleTestimonialImageError($event, index)" />
                                        <button type="button" @click="handleTestimonialImageClick(index)" 
                                            class="text-sm text-blue-400 hover:text-blue-300">
                                            Change Logo
                                        </button>
                                        <input :ref="el => testimonialImageInputs[index] = el"
                                            type="file" accept="image/jpeg,image/png,image/jpg"
                                            @change="handleTestimonialImageChange($event, index)" class="hidden" />
                                        <p v-if="testimonial.imageUploading" class="text-sm text-blue-400">Uploading...</p>
                                    </div>
                                    <div v-else class="flex flex-col items-center space-y-2">
                                        <button type="button" @click="handleTestimonialImageClick(index)" 
                                            class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm">
                                            Upload Logo
                                        </button>
                                        <input :ref="el => testimonialImageInputs[index] = el"
                                            type="file" accept="image/jpeg,image/png,image/jpg"
                                            @change="handleTestimonialImageChange($event, index)" class="hidden" />
                                        <p v-if="errors[`testimonialImage${index}`]" class="text-sm text-red-400">{{ errors[`testimonialImage${index}`] }}</p>
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label :for="`testimonialName${index}`" class="block text-sm font-medium mb-1">Client Name</label>
                                        <input v-model="testimonial.name" :id="`testimonialName${index}`" type="text"
                                            class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                    </div>
                                    <div>
                                        <label :for="`testimonialPosition${index}`" class="block text-sm font-medium mb-1">Position</label>
                                        <input v-model="testimonial.position" :id="`testimonialPosition${index}`" type="text"
                                            class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white" />
                                    </div>
                                    <div class="md:col-span-2">
                                        <label :for="`testimonialContent${index}`" class="block text-sm font-medium mb-1">Testimonial</label>
                                        <textarea v-model="testimonial.content" :id="`testimonialContent${index}`" rows="3"
                                            class="w-full bg-gray-700 border border-gray-600 rounded-md p-2 text-white"></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button type="button" @click="addTestimonial" 
                                class="w-full p-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
                            + Add Testimonial
                        </button>
                    </div>
                </section>

                <!-- Submit Button -->
                <div class="flex justify-end">
                    <button type="submit" :disabled="isSubmitting || imageUploading"
                        class="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 sm:py-2 sm:px-4 rounded-md disabled:opacity-50 min-h-[44px] min-w-[120px] touch-manipulation text-base sm:text-sm">
                        {{ isSubmitting ? 'Submitting...' : 'Submit' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { projectFirestore, projectStorage } from '@/firebase/config.js';
import { collection, addDoc, getDocs, query, limit, serverTimestamp, doc, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const form = ref({
    name: null,
    type: [],
    frontends: [],
    backends: [],
    databases: [],
    tools: [],
    graphics: [],
    aboutTxt: null,
    imageURL: null,
    imagePath: null,
    age: null,
    gender: null,
    nationality: null,
    available: null,
    phone: null,
    email: null,
    address: null,
    facebookURL: null,
    telegramURL: null,
    githubURL: null,
    linkedinURL: null,
    projectsCount: 0,
    experienceYears: 0,
    clientsCount: 0,
    certificationsCount: 0,
    experience: [],
    education: [],
    achievements: [],
    testimonials: [],
});
const heroDocId = ref(null);
const errors = ref({});
const typeInput = ref('');
const frontendsInput = ref('');
const backendsInput = ref('');
const databasesInput = ref('');
const toolsInput = ref('');
const graphicsInput = ref('');
const previewImage = ref(null);
const imageFile = ref(null);
const testimonialImageInputs = ref({});
const error = ref({
    type: '',
    frontends: '',
    backends: '',
    databases: '',
    tools: '',
    graphics: '',
});
const isSubmitting = ref(false);
const imageUploading = ref(false);
const isLoading = ref(false);

const handleTestimonialImageClick = (index) => {
    if (testimonialImageInputs.value[index]) {
        testimonialImageInputs.value[index].click();
    }
};

const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            errors.value.image = 'Please upload a jpg or png image';
            imageFile.value = null;
            previewImage.value = null;
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            errors.value.image = 'Image size must be 2MB or less';
            imageFile.value = null;
            previewImage.value = null;
            return;
        }
        errors.value.image = '';
        imageFile.value = file;
        const reader = new FileReader();
        reader.onload = (e) => {
            previewImage.value = e.target.result;
        };
        reader.readAsDataURL(file);
    } else {
        imageFile.value = null;
        previewImage.value = null;
    }
};

const addItem = (inputRef, arrayKey, errorKey) => (event) => {
    if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        const value = inputRef.value.trim();
        if (!value) {
            error.value[errorKey] = 'Input cannot be empty';
            return;
        }
        if (form.value[arrayKey].includes(value)) {
            error.value[errorKey] = 'This item already exists';
            return;
        }
        form.value[arrayKey] = [...form.value[arrayKey], value];
        inputRef.value = '';
        error.value[errorKey] = '';
    }
};

const removeItem = (arrayKey) => (index) => {
    form.value[arrayKey] = form.value[arrayKey].filter((_, i) => i !== index);
};

// Define specific remove functions for each array
const removeFrontends = removeItem('frontends');
const removeBackends = removeItem('backends');
const removeDatabases = removeItem('databases');
const removeTools = removeItem('tools');
const removeGraphics = removeItem('graphics');
const addType = addItem(typeInput, 'type', 'type');
const addFrontends = addItem(frontendsInput, 'frontends', 'frontends');
const addBackends = addItem(backendsInput, 'backends', 'backends');
const addDatabases = addItem(databasesInput, 'databases', 'databases');
const addTools = addItem(toolsInput, 'tools', 'tools');
const addGraphics = addItem(graphicsInput, 'graphics', 'graphics');

// Functions for managing experience entries
const addExperience = () => {
    form.value.experience.push({
        title: '',
        company: '',
        startDate: '',
        endDate: '',
        description: ''
    });
};

const removeExperience = (index) => {
    form.value.experience.splice(index, 1);
};

// Functions for managing education entries
const addEducation = () => {
    form.value.education.push({
        degree: '',
        institution: '',
        startDate: '',
        endDate: '',
        description: ''
    });
};

const removeEducation = (index) => {
    form.value.education.splice(index, 1);
};

// Functions for managing achievements entries
const addAchievement = () => {
    form.value.achievements.push({
        title: '',
        date: '',
        description: ''
    });
};

const removeAchievement = (index) => {
    form.value.achievements.splice(index, 1);
};

// Functions for managing testimonials entries
const addTestimonial = () => {
    form.value.testimonials.push({
        name: '',
        position: '',
        content: '',
        imageURL: null,
        imagePath: null,
        previewImage: null,
        imageFile: null,
        imageUploading: false
    });
};

const removeTestimonial = (index) => {
    // Delete image from storage if exists
    if (form.value.testimonials[index].imagePath) {
        const oldImageRef = storageRef(projectStorage, form.value.testimonials[index].imagePath);
        deleteObject(oldImageRef).catch(err => console.warn('Testimonial image deletion failed:', err));
    }
    form.value.testimonials.splice(index, 1);
};

const handleTestimonialImageChange = async (event, index) => {
    const file = event.target.files[0];
    if (file) {
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!validTypes.includes(file.type)) {
            errors.value[`testimonialImage${index}`] = 'Please upload a jpg or png image';
            form.value.testimonials[index].imageFile = null;
            form.value.testimonials[index].previewImage = null;
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            errors.value[`testimonialImage${index}`] = 'Image size must be 10MB or less';
            form.value.testimonials[index].imageFile = null;
            form.value.testimonials[index].previewImage = null;
            return;
        }
        errors.value[`testimonialImage${index}`] = '';
        form.value.testimonials[index].imageFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
            form.value.testimonials[index].previewImage = e.target.result;
        };
        reader.readAsDataURL(file);
    } else {
        form.value.testimonials[index].imageFile = null;
        form.value.testimonials[index].previewImage = null;
    }
};

const handleTestimonialImageError = (event, index) => {
    // Remove broken image URL and clear preview
    if (form.value.testimonials[index]) {
        form.value.testimonials[index].imageURL = null;
        form.value.testimonials[index].previewImage = null;
    }
    // Hide the image by removing it from display
    event.target.style.display = 'none';
};

const fetchData = async () => {
    isLoading.value = true;
    try {
        const heroesCollection = collection(projectFirestore, 'heroes');
        const q = query(heroesCollection, limit(1));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            heroDocId.value = doc.id;
            const docData = doc.data();
            form.value = { ...form.value, ...docData };
            previewImage.value = docData.imageURL || null;
            
            // Ensure testimonials have imageURL and imagePath fields
            if (form.value.testimonials && Array.isArray(form.value.testimonials)) {
                form.value.testimonials = form.value.testimonials.map(t => ({
                    ...t,
                    imageURL: t.imageURL || null,
                    imagePath: t.imagePath || null,
                    previewImage: null,
                    imageFile: null,
                    imageUploading: false
                }));
            }
        }
    } catch (err) {
        console.error('Error fetching hero:', err);
        alert('Failed to fetch data: ' + err.message);
    } finally {
        isLoading.value = false;
    }
};

const validateForm = () => {
    if (!form.value.name) {
        alert('Name is required');
        return false;
    }
    if (!form.value.age) {
        alert('Date of Birth is required');
        return false;
    }
    if (!form.value.email) {
        alert('Email is required');
        return false;
    }
    return true;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    isSubmitting.value = true;
    try {
        let imageURL = form.value.imageURL;
        let imagePath = form.value.imagePath;

        if (imageFile.value) {
            imageUploading.value = true;
            const uniqueId = Date.now();
            imagePath = `heroes/${uniqueId}_${imageFile.value.name}`;
            const imageRef = storageRef(projectStorage, imagePath);

            if (form.value.imagePath) {
                const oldImageRef = storageRef(projectStorage, form.value.imagePath);
                await deleteObject(oldImageRef).catch(err => console.warn('Old image deletion failed:', err));
            }

            const snapshot = await uploadBytes(imageRef, imageFile.value);
            imageURL = await getDownloadURL(snapshot.ref);
        }

        // Upload testimonial images
        for (let i = 0; i < form.value.testimonials.length; i++) {
            const testimonial = form.value.testimonials[i];
            if (testimonial.imageFile) {
                testimonial.imageUploading = true;
                const uniqueId = Date.now() + i;
                const testimonialImagePath = `heroes/testimonials/${uniqueId}_${testimonial.imageFile.name}`;
                const testimonialImageRef = storageRef(projectStorage, testimonialImagePath);

                // Delete old image if exists
                if (testimonial.imagePath) {
                    const oldImageRef = storageRef(projectStorage, testimonial.imagePath);
                    await deleteObject(oldImageRef).catch(err => console.warn('Old testimonial image deletion failed:', err));
                }

                const snapshot = await uploadBytes(testimonialImageRef, testimonial.imageFile);
                testimonial.imageURL = await getDownloadURL(snapshot.ref);
                testimonial.imagePath = testimonialImagePath;
                testimonial.imageUploading = false;
            }
            // Remove temporary properties before saving
            delete testimonial.previewImage;
            delete testimonial.imageFile;
            delete testimonial.imageUploading;
        }

        const data = {
            ...form.value,
            imageURL,
            imagePath,
            createdAt: serverTimestamp(),
        };

        if (heroDocId.value) {
            const heroRef = doc(projectFirestore, 'heroes', heroDocId.value);
            await setDoc(heroRef, data, { merge: true });
        } else {
            const heroRef = collection(projectFirestore, 'heroes');
            const docRef = await addDoc(heroRef, data);
            heroDocId.value = docRef.id;
        }

        alert('Hero saved successfully!');
    } catch (error) {
        console.error('Error saving hero:', error);
        alert('Error saving hero. Please try again.');
    } finally {
        isSubmitting.value = false;
        imageUploading.value = false;
    }
};

onMounted(() => {
    fetchData();
});
</script>